import { useEffect, useState, useCallback, useMemo } from "react";
import { TABS_CONFIG } from "../../globalConstants/TabConfig";
import { LoanApplicationService } from "../../services/LoanApplication";
import { getVisibleTabRoute } from "../../services/TabVisibility";
import { LENDER_IDs } from "../utils/Global";

interface UiPermission {
  tabs: {
    [key: string]: {
      isVisible?: boolean;
    };
  };
  sidebar: {
    buttons: {
      [key: string]: {
        isVisible?: boolean;
      };
    };
  };
}

interface Tab {
  name: string;
  isVisible?: boolean;
  [key: string]: any;
}

/**
 * Custom hook: useDerivedTabsFromUiPermissions
 *
 * Purpose:
 * Fetches UI tab permissions based on a loan ID and token from the backend,
 * and derives a list of visible tabs for the UI based on those permissions
 * and a static TABS_CONFIG map.
 *
 * How it works:
 * 1. Calls `LoanApplicationService.getPolicyUiConfig()` to get tab permissions.
 * 2. Maps those permissions to the corresponding tab definitions in TABS_CONFIG.
 * 3. Returns only those tabs that are marked as `isVisible`.
 *
 * Dependencies: React (useState, useEffect, useCallback, useMemo), LoanApplicationService, TABS_CONFIG
 *
 * @param loanID - Unique ID of the loan
 * @param token - Authentication token
 * @returns An object containing visible Tab objects, sidebar permissions, and raw UI permissions
 */
export function useDerivedTabsFromUiPermissions(
  loanID: string,
  token: string,
  lenderID: string,
  isPlatformDashboard: boolean,
  pageName?: string
): {
  tabs: Tab[];
  sidebar: UiPermission["sidebar"];
  uiPermissions: UiPermission;
} {
  const emptyObj = {
    tabs: {},
    sidebar: {
      buttons: {}
    }
  };
  const [permissions, setPermissions] = useState<UiPermission>(emptyObj);

  /**
   * Fetches tab visibility permissions for the given loanID and token.
   * @returns A map of tab permissions from the backend, keyed by tab name
   */
  const fetchPermissions = useCallback(async (): Promise<UiPermission> => {
    if (
      !loanID ||
      !token ||
      (lenderID !== LENDER_IDs.MFL_LAP && lenderID !== LENDER_IDs.AXIS_NEW)
    )
      return emptyObj;

    try {
      const response: any = await LoanApplicationService.getPolicyUiConfig({
        loanID,
        token,
        isPlatformDashboard,
        pageName
      });

      return response?.status && response?.data ? response.data : emptyObj;
    } catch (error) {
      console.error("Error fetching permissions", error);

      return emptyObj;
    }
  }, [loanID, token, isPlatformDashboard]);

  /**
   * Fetch permissions on mount or when loanID/token changes.
   */
  useEffect(() => {
    if (loanID && token) {
      fetchPermissions().then((data) => {
        setPermissions({
          sidebar: data?.sidebar || { buttons: {} },
          tabs: data?.tabs || {}
        });
      });
    }
  }, [fetchPermissions]);

  /**
   * Memoize the derived tabs to prevent unnecessary re-renders
   */
  const derivedTabs = useMemo(() => {
    const visibleTabs: Tab[] = [];

    for (const tabKey in permissions?.tabs) {
      const tabPermission = permissions?.tabs?.[tabKey];

      // Format the key to match the naming in TABS_CONFIG
      const modifiedTabKey = tabKey.replace(/_/g, " ").toUpperCase();

      if (tabPermission?.isVisible && TABS_CONFIG?.[modifiedTabKey]) {
        const staticTabConfig = TABS_CONFIG?.[modifiedTabKey];
        const tab = {
          ...staticTabConfig,
          ...tabPermission,
          href: getVisibleTabRoute({
            tabId: tabKey,
            tabPermission,
            fallbackRoute: staticTabConfig.href
          })
        };

        visibleTabs.push(tab);
      }
    }

    // Sort tabs by the `order` key
    visibleTabs.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

    return visibleTabs;
  }, [permissions?.tabs]);

  /**
   * Memoize the return value to prevent unnecessary re-renders
   */
  const result = useMemo(
    () => ({
      tabs: derivedTabs,
      sidebar: permissions?.sidebar,
      uiPermissions: permissions
    }),
    [derivedTabs, permissions?.sidebar, permissions]
  );

  return result;
}
